<template>
  <Dialog
    v-model:visible="visible"
    :style="{ width: '600px' }"
    header="Import Questions"
    :modal="true"
  >
    <div class="field">
      <label>CSV file</label>
      <FileUpload
        ref="uploader"
        accept=".csv,text/csv"
        custom-upload
        :auto="false"
        class="block mt-1"
        :pt="{
          header: { class: 'hidden' },
          root: { class: 'border-none' },
          content: { class: 'border-none p-0' }
        }"
        @select="onFileSelect"
        @remove="resetParsed"
      >
        <template #content="{ files, removeFileCallback }">
          <div
            v-for="(file, i) of files"
            :key="file.name + file.size"
            class="flex align-items-center gap-3 px-3 py-2 border-round surface-100"
          >
            <i class="pi pi-file text-primary flex-shrink-0" />
            <div
              class="flex-1"
              style="min-width: 0"
            >
              <div class="text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                {{ file.name }}
              </div>
              <div class="text-xs text-color-secondary">
                {{ formatSize(file.size) }}
              </div>
            </div>
            <Button
              icon="pi pi-times"
              class="p-button-rounded p-button-text p-button-secondary"
              @click="removeFileCallback(i)"
            />
          </div>
        </template>
        <template #empty>
          <div
            class="flex flex-column align-items-center gap-2 py-5 cursor-pointer border-1 border-dashed border-round surface-border text-color-secondary"
            @click="chooseFile"
          >
            <i
              class="pi pi-cloud-upload"
              style="font-size: 2rem"
            />
            <div class="text-sm font-medium">
              Drop a CSV file or click to browse
            </div>
          </div>
        </template>
      </FileUpload>
      <small class="block mt-2 text-color-secondary">
        Expected columns: <b>question</b>, <b>answer</b>, <b>group</b>. Groups are
        matched to your existing groups by name; unmatched groups are created.
      </small>
    </div>

    <div
      v-if="groupMappings.length"
      class="field"
    >
      <label>Group mapping</label>
      <div
        v-for="mapping in groupMappings"
        :key="mapping.source"
        class="flex align-items-center gap-2 mb-2"
      >
        <span class="flex-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">
          {{ mapping.source || '(no group)' }}
        </span>
        <i class="pi pi-arrow-right text-color-secondary" />
        <Dropdown
          v-model="mapping.target"
          :options="groupOptionsFor(mapping)"
          option-label="label"
          option-value="value"
          placeholder="Select a group"
          class="flex-1"
        />
      </div>
    </div>

    <div
      v-if="rows.length"
      class="text-color-secondary"
    >
      {{ rows.length }} question(s) ready to import.
    </div>

    <template #footer>
      <Button
        label="Cancel"
        icon="pi pi-times"
        class="p-button-text"
        :disabled="importing"
        @click="visible = false"
      />
      <Button
        label="Import"
        icon="pi pi-upload"
        class="p-button-text"
        :loading="importing"
        :disabled="!rows.length"
        @click="importCsv"
      />
    </template>
  </Dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'primevue/usetoast'

const emit = defineEmits(['imported'])

const visible = defineModel('visible', { type: Boolean, default: false })

const store = useStore()
const toast = useToast()

const groups = computed(() => store.state.groups.items)

const uploader = ref()
const rows = ref([])
const groupMappings = ref([])
const importing = ref(false)

watch(visible, open => {
  if (open) resetParsed()
})

function resetParsed () {
  rows.value = []
  groupMappings.value = []
}

function chooseFile () {
  uploader.value.choose()
}

function formatSize (bytes) {
  if (!bytes) return '0 B'

  const k = 1024
  const sizes = ['B', 'KB', 'MB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

function onFileSelect (event) {
  const file = event.files && event.files[0]
  if (!file) return

  const reader = new FileReader()
  reader.onload = () => {
    try {
      loadCsv(String(reader.result))
    } catch (err) {
      resetParsed()
      toast.add({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to read CSV', life: 3000 })
    }
  }
  reader.readAsText(file)
}

// parseCsv turns RFC 4180 CSV text into an array of string rows, honoring
// quoted fields, escaped quotes, and embedded newlines.
function parseCsv (text) {
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1)

  const result = []
  let row = []
  let field = ''
  let quoted = false

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++ } else { quoted = false }
      } else {
        field += ch
      }
    } else if (ch === '"') {
      quoted = true
    } else if (ch === ',') {
      row.push(field); field = ''
    } else if (ch === '\n') {
      row.push(field); field = ''
      result.push(row); row = []
    } else if (ch !== '\r') {
      field += ch
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field)
    result.push(row)
  }
  return result
}

function loadCsv (text) {
  const parsed = parseCsv(text).filter(r => r.some(cell => cell.trim() !== ''))
  if (!parsed.length) throw new Error('The CSV file is empty')

  const header = parsed.shift().map(h => h.trim().toLowerCase())
  const questionIdx = header.indexOf('question')
  const answerIdx = header.indexOf('answer')
  const groupIdx = header.indexOf('group')
  if (questionIdx === -1 || answerIdx === -1) {
    throw new Error('The CSV must have "question" and "answer" columns')
  }

  rows.value = parsed
    .map(r => ({
      group: groupIdx === -1 ? '' : (r[groupIdx] || '').trim(),
      question: (r[questionIdx] || '').trim(),
      answer: (r[answerIdx] || '').trim()
    }))
    .filter(r => r.question && r.answer)

  buildGroupMappings()
}

// parseGroupCell splits an exported "[id] title" group cell into its parts,
// falling back to treating the whole cell as a plain title.
function parseGroupCell (cell) {
  const match = cell.match(/^\[(\d+)\]\s*(.*)$/)
  if (match) {
    return { idHint: Number(match[1]), title: match[2].trim() }
  }
  return { idHint: null, title: cell.trim() }
}

function buildGroupMappings () {
  const distinct = [...new Set(rows.value.map(r => r.group))]

  groupMappings.value = distinct.map(source => {
    const { idHint, title } = parseGroupCell(source)
    const byId = idHint != null && groups.value.find(g => g.id === idHint)
    const byTitle = groups.value.find(g => g.title.toLowerCase() === title.toLowerCase())

    let target = null
    if (byId) target = byId.id
    else if (byTitle) target = byTitle.id
    else if (title) target = '__new__'

    return { source, title, target }
  })
}

function groupOptionsFor (mapping) {
  const options = groups.value.map(g => ({ label: g.title, value: g.id }))
  if (mapping.title) {
    options.push({ label: `Create new: "${mapping.title}"`, value: '__new__' })
  }
  return options
}

async function importCsv () {
  importing.value = true
  try {
    const resolvedBySource = new Map()
    const createdByTitle = new Map()

    for (const mapping of groupMappings.value) {
      if (mapping.target === '__new__') {
        const key = mapping.title.toLowerCase()
        let groupId = createdByTitle.get(key)
        if (!groupId) {
          const group = await store.dispatch('groups/createGroup', { title: mapping.title })
          groupId = group.id
          createdByTitle.set(key, groupId)
        }
        resolvedBySource.set(mapping.source, groupId)
      } else if (mapping.target) {
        resolvedBySource.set(mapping.source, mapping.target)
      }
    }

    const items = rows.value
      .map(r => ({ group_id: resolvedBySource.get(r.group), question: r.question, answer: r.answer }))
      .filter(it => it.group_id)

    if (!items.length) {
      toast.add({ severity: 'warn', summary: 'Nothing to import', detail: 'No rows are mapped to a group', life: 3000 })
      return
    }

    const result = await store.dispatch('questions/importQuestions', { items })

    visible.value = false
    emit('imported')

    const detail = result.duplicates
      ? `Imported ${result.imported} question(s), ${result.duplicates} duplicate(s) skipped`
      : `Imported ${result.imported} question(s)`
    toast.add({ severity: 'success', summary: 'Successful', detail, life: 3000 })
  } catch (err) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to import questions', life: 3000 })
    console.error('Import error:', err)
  } finally {
    importing.value = false
  }
}
</script>
